import * as Sentry from "@sentry/react";
import config from "../config";

const { localurl, environmentName, sentryDsn } = config || {};

export const init = () => {
  Sentry.init({
    dsn: sentryDsn,
    integrations: [
      new Sentry.BrowserTracing({
        tracePropagationTargets: [localurl],
      }),
      new Sentry.Replay(),
    ],
    environment: environmentName || "unknown",
    // Injected at build time by @sentry/vite-plugin from the git commit SHA.
    // Sentry uses this to look up the correct source maps for the deployed build.
    release: import.meta.env.SENTRY_RELEASE,
    tracesSampleRate: 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0.1,
  });
};

export const setSentryUser = async (user = null) => {
  if (user !== null && user?.email) {
    Sentry.setUser({
      username: user?.name,
      email: user?.email,
      ip_address: "{{auto}}",
    });
  }
};

// Convert error-like plain objects (e.g. serialized AxiosError) back to real Error instances.
// This prevents the JSON blob title problem: when instanceof Error fails on a bundled AxiosError,
// safeStringify produces {"name":"AxiosError","message":"..."} as the Sentry event title.
const normalizeToError = (value) => {
  if (value instanceof Error) return value;
  if (
    value &&
    typeof value === "object" &&
    typeof value.message === "string" &&
    (value.name || value.stack)
  ) {
    const err = new Error(value.message);
    err.name = value.name || "Error";
    if (value.stack) err.stack = value.stack;
    return err;
  }
  return null;
};

// --- Extract first caller stack frame outside logService ---
const getStackLocation = () => {
  const stackLines = new Error().stack?.split("\n") || [];
  const ignorePatterns = ["logService.js", "@sentry", "node_modules"];

  for (const line of stackLines) {
    const trimmed = line.trim();
    if (!trimmed.startsWith("at ")) continue;
    if (ignorePatterns.some((pattern) => trimmed.includes(pattern))) continue;

    const withMethod = trimmed.match(/at (.+?) \((.+):(\d+):(\d+)\)/);
    if (withMethod) {
      return {
        method: withMethod[1],
        file: withMethod[2],
        line: withMethod[3],
        column: withMethod[4],
      };
    }

    const withoutMethod = trimmed.match(/at (.+):(\d+):(\d+)/);
    if (withoutMethod) {
      return {
        method: "anonymous",
        file: withoutMethod[1],
        line: withoutMethod[2],
        column: withoutMethod[3],
      };
    }
  }

  return {};
};

const safeStringify = (value) => {
  if (value instanceof Error) return value.message;
  if (typeof value === "string") return value;
  if (value === null || value === undefined) return String(value);
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch (err) {
      return "[Unserializable Object]";
    }
  }
  return String(value);
};

const normalizePayload = (payload) => {
  if (!payload) return {};
  if (payload instanceof Error) {
    return { payloadError: payload.message };
  }
  if (typeof payload === "object" && !Array.isArray(payload)) {
    return payload;
  }
  return { payloadValue: payload };
};

// --- Map error messages to relevant titles ---
const HTTP_STATUS_TITLES = {
  400: "Bad Request",
  401: "Unauthorized Request",
  402: "Payment Required",
  403: "Forbidden Request",
  404: "Resource Not Found",
  405: "Method Not Allowed",
  406: "Not Acceptable",
  408: "Request Timeout",
  409: "Conflict",
  410: "Resource Gone",
  411: "Length Required",
  412: "Precondition Failed",
  413: "Payload Too Large",
  414: "URI Too Long",
  415: "Unsupported Media Type",
  422: "Validation Error",
  423: "Resource Locked",
  425: "Too Early",
  428: "Precondition Required",
  429: "Too Many Requests",
  431: "Request Headers Too Large",
  451: "Unavailable For Legal Reasons",
  500: "Server Error",
  501: "Not Implemented",
  502: "Bad Gateway",
  503: "Service Unavailable",
  504: "Gateway Timeout",
  505: "HTTP Version Not Supported",
  507: "Insufficient Storage",
  508: "Loop Detected",
  511: "Network Authentication Required",
};

// Prefer the explicit status on the error (AxiosError.response.status / .status),
// then "status code NNN" in the message, then a standalone 4xx/5xx number.
const extractStatusCode = (error, msg) => {
  const direct = error?.response?.status ?? error?.status;
  if (Number.isInteger(direct)) return direct;

  const statusMatch = msg.match(/status code (\d{3})/i);
  if (statusMatch) return Number(statusMatch[1]);

  const codeMatch = msg.match(/\b([45]\d{2})\b/);
  return codeMatch ? Number(codeMatch[1]) : null;
};

const getErrorTitle = (error) => {
  if (error?.name === "ApiFirewallError") return "42Crunch Firewall Rejection";

  const msg = error instanceof Error ? error.message : safeStringify(error);

  const status = extractStatusCode(error, msg);
  if (status != null) {
    if (HTTP_STATUS_TITLES[status]) return HTTP_STATUS_TITLES[status];
    if (status >= 500) return "Server Error";
    if (status >= 400) return "Client Error";
  }

  if (msg.includes("Network Error")) return "Network Failure";
  if (/timeout of \d+ms exceeded|ECONNABORTED|ETIMEDOUT/i.test(msg)) return "Request Timeout";
  if (/\bcanceled\b|ERR_CANCELED|AbortError/i.test(msg)) return "Request Cancelled";
  if (/ChunkLoadError|Loading chunk|dynamically imported module/i.test(msg)) return "Chunk Load Failure";

  return "Application Error";
};

const getLogTitle = (level) => {
  switch (level) {
    case "info":
      return "Information Log";
    case "warning":
      return "Warning Alert";
    case "debug":
      return "Debug Log";
    case "log":
      return "General Log";
    default:
      return "Application Log";
  }
};

// Apply API-specific Sentry tags when the payload contains HTTP context keys.
// This enables filtering in Sentry by endpoint, HTTP method, and status code.
const applyApiTags = (scope, payload) => {
  if (payload.method) scope.setTag("api.method", String(payload.method).toUpperCase());
  if (payload.status != null) scope.setTag("api.status", String(payload.status));
  if (payload.url) scope.setTag("api.endpoint", payload.url);
  if (payload.route) scope.setTag("route", payload.route);
  if (payload.component) scope.setTag("component", payload.component);
  if (payload.correlationId) scope.setTag("correlationId", payload.correlationId);
};

// Sentry trims context objects deeper than 3 levels to "[Object]", so a
// rejected payload is kept as a string in the context and as a file attachment.
const MAX_CONTEXT_BODY_LENGTH = 100000;

const applyApiFirewallContext = (scope, apiFirewall, rawRequestBody, method, url) => {
  const body =
    typeof rawRequestBody === "string" ? rawRequestBody : safeStringify(rawRequestBody);

  scope.setTag("api.firewall", "42crunch");
  if (apiFirewall.uuid) scope.setTag("api.firewall.uuid", apiFirewall.uuid);
  // One Sentry issue per rejected endpoint instead of one per call site.
  scope.setFingerprint([
    "42crunch-firewall",
    String(method || "").toUpperCase(),
    String(url || "").split("?")[0],
  ]);
  scope.setContext("42Crunch Firewall", {
    title: apiFirewall.title,
    detail: apiFirewall.detail,
    uuid: apiFirewall.uuid,
    originalRequestBody:
      body.length > MAX_CONTEXT_BODY_LENGTH
        ? `${body.slice(0, MAX_CONTEXT_BODY_LENGTH)}… [truncated, see attachment]`
        : body,
  });
  scope.addAttachment({
    filename: "42crunch-rejected-request-body.json",
    data: body,
    contentType: "application/json",
  });
};

const logWithPayload = (message, payload = {}, user = null) => {
  // The HTTP interceptor in httpService.js already captured this error with
  // full API context. Skip it here to prevent a duplicate, lower-quality event
  // from component-level catch handlers that receive the same rejected promise.
  if (message?.__sentryLogged) return;

  Sentry.withScope((scope) => {
    if (user) {
      scope.setUser({
        username: user?.name,
        email: user?.email,
        ip_address: "{{auto}}",
      });
    }

    const normalizedPayload = normalizePayload(payload);
    if (Object.keys(normalizedPayload).length > 0) {
      // Group API context into structured Sentry contexts instead of flat extras
      const { method, url, baseURL, params, data, headers, responseData, status, route, component, correlationId, apiFirewall, rawRequestBody, ...rest } = normalizedPayload;

      if (apiFirewall) {
        applyApiFirewallContext(scope, apiFirewall, rawRequestBody, method, url);
      }

      if (method || url || status != null) {
        scope.setContext("API Request", {
          method: method ? String(method).toUpperCase() : undefined,
          url: baseURL && url ? `${baseURL}${url}` : url,
          params,
          body: data,
        });
        scope.setContext("API Response", {
          status,
          data: responseData,
        });
        if (headers) {
          scope.setContext("Request Headers", headers);
        }
      }

      if (Object.keys(rest).length > 0) {
        scope.setExtras(rest);
      }

      applyApiTags(scope, normalizedPayload);
    }

    // Always tag the current browser route for non-API errors too
    if (!payload.route && typeof window !== "undefined") {
      scope.setTag("route", window.location.pathname);
    }

    const location = getStackLocation();
    if (Object.keys(location).length > 0) {
      scope.setContext("Caller", location);
      if (location.file) {
        scope.setTag("callerFile", location.file);
      }
    }

    // Normalize error-like plain objects so they always hit captureException
    const normalized = normalizeToError(message);
    const errorToCapture = normalized || message;

    if (errorToCapture instanceof Error) {
      const title = getErrorTitle(errorToCapture);
      scope.setExtra("errorName", errorToCapture.name);
      // Only prepend title if not already present to avoid double-prefixing
      if (!errorToCapture.message.startsWith(title)) {
        errorToCapture.message = `${title}: ${errorToCapture.message}`;
      }
      Sentry.captureException(errorToCapture);
    } else {
      const title = getErrorTitle(message);
      const msg = safeStringify(message);
      scope.setExtra("errorType", typeof message);
      scope.setExtra("originalMessage", message);
      Sentry.captureMessage(`${title}: ${msg}`, "error");
    }
  });
};

const logWithLevel = (level, message, user = null, payload = {}) => {
  if (message?.__sentryLogged) return;

  Sentry.withScope((scope) => {
    if (user) {
      scope.setUser({
        username: user?.name,
        email: user?.email,
        ip_address: "{{auto}}",
      });
    }

    const normalizedPayload = normalizePayload(payload);
    if (Object.keys(normalizedPayload).length > 0) {
      scope.setExtras(normalizedPayload);
      applyApiTags(scope, normalizedPayload);
    }

    if (typeof window !== "undefined") {
      scope.setTag("route", window.location.pathname);
    }

    const location = getStackLocation();
    if (Object.keys(location).length > 0) {
      scope.setContext("Caller", location);
      if (location.file) {
        scope.setTag("callerFile", location.file);
      }
    }

    // Normalize error-like plain objects so they always hit captureException
    const normalized = normalizeToError(message);
    const errorToCapture = normalized || message;

    if (errorToCapture instanceof Error) {
      const title = getErrorTitle(errorToCapture);
      scope.setExtra("errorName", errorToCapture.name);
      if (!errorToCapture.message.startsWith(title)) {
        errorToCapture.message = `${title}: ${errorToCapture.message}`;
      }
      Sentry.captureException(errorToCapture);
      return;
    }

    const title = getErrorTitle(message);
    const msg = safeStringify(message);
    scope.setExtra("errorType", typeof message);
    scope.setExtra("originalMessage", message);
    Sentry.captureMessage(`${title}: ${msg}`, level);
  });
};

export const log = async (error, user = null) => {
  logWithLevel("log", error, user);
};

export const logInfo = async (error, user = null) => {
  logWithLevel("info", error, user);
};

export const logWarning = async (error, user = null) => {
  logWithLevel("warning", error, user);
};

export const logDebug = async (error, user = null) => {
  logWithLevel("debug", error, user);
};

export const logError = async (error, user = null) => {
  logWithLevel("error", error, user);
};

export const logErrorWithPayload = async (error, payload = {}, user = null) => {
  logWithPayload(error, payload, user);
};

const logger = {
  init,
  log,
  logError,
  logInfo,
  logWarning,
  logDebug,
  setSentryUser,
};
export default logger;
